import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import {
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Eye,
  Unlock,
  FileCheck,
  Calendar,
  DollarSign,
  User,
  Search,
  RefreshCw,
  ArrowLeft
} from 'lucide-react';
import BackButton from './ui/BackButton';

const GestionCompromisosPago = () => {
  const { API, user } = useContext(AuthContext);
  const [compromisos, setCompromisos] = useState([]);
  const [resumen, setResumen] = useState({
    pendientes: 0,
    por_verificar: 0,
    vencidos: 0,
    pagados: 0,
    total: 0
  });
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [isVerificarDialogOpen, setIsVerificarDialogOpen] = useState(false);
  const [isDesbloquearDialogOpen, setIsDesbloquearDialogOpen] = useState(false);
  const [isVerComprobanteOpen, setIsVerComprobanteOpen] = useState(false);
  const [selectedCompromiso, setSelectedCompromiso] = useState(null);
  const [notasAdmin, setNotasAdmin] = useState('');
  const [motivoDesbloqueo, setMotivoDesbloqueo] = useState('');

  useEffect(() => {
    loadCompromisos();
  }, [filtroEstado]);

  const loadCompromisos = async () => {
    try {
      setLoading(true);
      const url = filtroEstado === 'all' 
        ? `${API}/admin/compromisos-pago`
        : `${API}/admin/compromisos-pago?estado=${filtroEstado}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCompromisos(data.compromisos || []);
        setResumen(data.resumen || {});
      } else {
        toast.error('Error al cargar compromisos');
      }
    } catch (error) {
      console.error('Error loading compromisos:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificarClick = (compromiso) => {
    setSelectedCompromiso(compromiso);
    setNotasAdmin('');
    setIsVerificarDialogOpen(true);
  };

  const handleVerificar = async (aprobado) => {
    if (!selectedCompromiso) return;

    try {
      const response = await fetch(`${API}/admin/compromiso-pago/${selectedCompromiso.id}/verificar`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          aprobado,
          notas: notasAdmin || null
        })
      });

      if (response.ok) {
        toast.success(aprobado ? 'Pago aprobado correctamente' : 'Comprobante rechazado');
        setIsVerificarDialogOpen(false);
        loadCompromisos();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al verificar');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    }
  };

  const handleDesbloquearClick = (compromiso) => {
    setSelectedCompromiso(compromiso);
    setMotivoDesbloqueo('');
    setIsDesbloquearDialogOpen(true);
  };

  const handleDesbloquear = async () => {
    if (!selectedCompromiso || !motivoDesbloqueo.trim()) {
      toast.error('Debes indicar un motivo');
      return;
    }

    try {
      const response = await fetch(`${API}/admin/users/${selectedCompromiso.estudiante_id}/desbloquear-pago`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          motivo: motivoDesbloqueo
        })
      });

      if (response.ok) {
        toast.success('Usuario desbloqueado correctamente');
        setIsDesbloquearDialogOpen(false);
        loadCompromisos();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al desbloquear');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    }
  };

  const handleVerComprobante = (compromiso) => {
    setSelectedCompromiso(compromiso);
    setIsVerComprobanteOpen(true);
  };

  const getEstadoBadge = (estado, vencido) => {
    if (vencido && estado === 'pendiente') {
      return <Badge className="bg-red-500 text-white"><AlertTriangle className="w-3 h-3 mr-1" />Vencido</Badge>;
    }
    switch (estado) {
      case 'pendiente':
        return <Badge className="bg-yellow-500 text-white"><Clock className="w-3 h-3 mr-1" />Pendiente</Badge>;
      case 'pendiente_verificacion':
        return <Badge className="bg-blue-500 text-white"><Eye className="w-3 h-3 mr-1" />Por Verificar</Badge>;
      case 'pagado':
        return <Badge className="bg-green-500 text-white"><CheckCircle className="w-3 h-3 mr-1" />Pagado</Badge>;
      case 'vencido':
        return <Badge className="bg-red-500 text-white"><AlertTriangle className="w-3 h-3 mr-1" />Vencido</Badge>;
      case 'rechazado':
        return <Badge className="bg-orange-500 text-white"><XCircle className="w-3 h-3 mr-1" />Rechazado</Badge>;
      case 'cancelado':
        return <Badge className="bg-gray-500 text-white">Cancelado</Badge>;
      default:
        return <Badge>{estado}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-UY', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const filteredCompromisos = compromisos.filter(c => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      c.estudiante_nombre?.toLowerCase().includes(search) ||
      c.estudiante_email?.toLowerCase().includes(search) ||
      c.semestre_nombre?.toLowerCase().includes(search)
    );
  });

  if (user?.rol !== 'admin') {
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
        <h2 className="text-xl font-bold text-gray-700">Acceso Restringido</h2>
        <p className="text-gray-500">Solo administradores pueden ver esta página.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="gestion-compromisos-pago">
      <BackButton />
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CreditCard className="w-7 h-7 text-indigo-600" />
            Compromisos de Pago
          </h1>
          <p className="text-gray-600 mt-1">Gestiona los compromisos de pago de estudiantes</p>
        </div>
        <Button onClick={loadCompromisos} variant="outline" className="gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="cursor-pointer hover:bg-gray-50" onClick={() => setFiltroEstado('all')}>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-gray-900">{resumen.total}</div>
            <div className="text-sm text-gray-600">Total</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-yellow-50 border-yellow-200" onClick={() => setFiltroEstado('pendiente')}>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-yellow-600">{resumen.pendientes}</div>
            <div className="text-sm text-gray-600">Pendientes</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-blue-50 border-blue-200" onClick={() => setFiltroEstado('pendiente_verificacion')}>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-blue-600">{resumen.por_verificar}</div>
            <div className="text-sm text-gray-600">Por Verificar</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-red-50 border-red-200" onClick={() => setFiltroEstado('vencido')}>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-red-600">{resumen.vencidos}</div>
            <div className="text-sm text-gray-600">Vencidos</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-green-50 border-green-200" onClick={() => setFiltroEstado('pagado')}>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-green-600">{resumen.pagados}</div>
            <div className="text-sm text-gray-600">Pagados</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Buscar por nombre, email o semestre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-compromisos"
          />
        </div>
        <Select value={filtroEstado} onValueChange={setFiltroEstado}>
          <SelectTrigger className="w-full md:w-48" data-testid="filter-estado">
            <SelectValue placeholder="Filtrar por estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pendiente">Pendientes</SelectItem>
            <SelectItem value="pendiente_verificacion">Por Verificar</SelectItem>
            <SelectItem value="vencido">Vencidos</SelectItem>
            <SelectItem value="pagado">Pagados</SelectItem>
            <SelectItem value="rechazado">Rechazados</SelectItem>
            <SelectItem value="cancelado">Cancelados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              <p className="mt-2 text-gray-500">Cargando compromisos...</p>
            </div>
          ) : filteredCompromisos.length === 0 ? (
            <div className="p-8 text-center">
              <CreditCard className="w-12 h-12 mx-auto text-gray-300" />
              <p className="mt-2 text-gray-500">No hay compromisos de pago</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Estudiante</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Semestre</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Monto</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Vencimiento</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Estado</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredCompromisos.map((compromiso) => (
                    <tr key={compromiso.id} className="hover:bg-gray-50" data-testid={`compromiso-row-${compromiso.id}`}>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{compromiso.estudiante_nombre}</p>
                          <p className="text-sm text-gray-500">{compromiso.estudiante_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {compromiso.semestre_nombre || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-gray-900">${compromiso.monto?.toFixed(2)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm text-gray-700">{formatDate(compromiso.fecha_vencimiento)}</p>
                          {compromiso.dias_restantes !== undefined && compromiso.estado === 'pendiente' && (
                            <p className={`text-xs ${compromiso.dias_restantes < 0 ? 'text-red-600 font-semibold' : compromiso.dias_restantes <= 3 ? 'text-yellow-600' : 'text-gray-500'}`}>
                              {compromiso.dias_restantes < 0 
                                ? `Vencido hace ${Math.abs(compromiso.dias_restantes)} días`
                                : compromiso.dias_restantes === 0
                                  ? 'Vence hoy'
                                  : `${compromiso.dias_restantes} días restantes`
                              }
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {getEstadoBadge(compromiso.estado, compromiso.vencido)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {/* Ver comprobante */}
                          {compromiso.comprobante_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleVerComprobante(compromiso)}
                              className="gap-1"
                              data-testid={`ver-comprobante-${compromiso.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {/* Verificar pago */}
                          {compromiso.estado === 'pendiente_verificacion' && (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleVerificarClick(compromiso)}
                              className="gap-1 bg-blue-600 hover:bg-blue-700"
                              data-testid={`verificar-${compromiso.id}`}
                            >
                              <FileCheck className="w-4 h-4" />
                              Verificar
                            </Button>
                          )}
                          
                          {/* Desbloquear */}
                          {(compromiso.estado === 'vencido' || compromiso.vencido) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDesbloquearClick(compromiso)}
                              className="gap-1 text-green-600 border-green-300 hover:bg-green-50"
                              data-testid={`desbloquear-${compromiso.id}`}
                            >
                              <Unlock className="w-4 h-4" />
                              Desbloquear
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal: Ver Comprobante */}
      <Dialog open={isVerComprobanteOpen} onOpenChange={setIsVerComprobanteOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Comprobante de Pago</DialogTitle>
            <DialogDescription>
              {selectedCompromiso?.estudiante_nombre} - ${selectedCompromiso?.monto?.toFixed(2)}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {selectedCompromiso?.comprobante_url && (
              <img
                src={selectedCompromiso.comprobante_url}
                alt="Comprobante de pago"
                className="max-w-full max-h-[60vh] mx-auto rounded-lg border"
              />
            )}
            {selectedCompromiso?.comprobante_fecha && (
              <p className="text-sm text-gray-500 mt-2 text-center">
                Subido el: {formatDate(selectedCompromiso.comprobante_fecha)}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsVerComprobanteOpen(false)}>
              Cerrar
            </Button>
            {selectedCompromiso?.estado === 'pendiente_verificacion' && (
              <Button 
                onClick={() => {
                  setIsVerComprobanteOpen(false);
                  handleVerificarClick(selectedCompromiso);
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Verificar Pago
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Verificar Pago */}
      <Dialog open={isVerificarDialogOpen} onOpenChange={setIsVerificarDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verificar Comprobante de Pago</DialogTitle>
            <DialogDescription>
              Revisa el comprobante y decide si apruebas o rechazas el pago.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <p><strong>Estudiante:</strong> {selectedCompromiso?.estudiante_nombre}</p>
              <p><strong>Monto:</strong> ${selectedCompromiso?.monto?.toFixed(2)}</p>
              <p><strong>Semestre:</strong> {selectedCompromiso?.semestre_nombre}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Notas (opcional)</label>
              <Textarea
                placeholder="Añade notas sobre la verificación..."
                value={notasAdmin}
                onChange={(e) => setNotasAdmin(e.target.value)}
                className="mt-1"
                data-testid="notas-verificacion"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsVerificarDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive"
              onClick={() => handleVerificar(false)}
              className="gap-1"
              data-testid="btn-rechazar-pago"
            >
              <XCircle className="w-4 h-4" />
              Rechazar
            </Button>
            <Button 
              onClick={() => handleVerificar(true)}
              className="gap-1 bg-green-600 hover:bg-green-700"
              data-testid="btn-aprobar-pago"
            >
              <CheckCircle className="w-4 h-4" />
              Aprobar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Desbloquear Usuario */}
      <Dialog open={isDesbloquearDialogOpen} onOpenChange={setIsDesbloquearDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Desbloquear Usuario</DialogTitle>
            <DialogDescription>
              Esta acción desbloqueará el acceso del estudiante y cancelará su compromiso de pago pendiente.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <p className="text-yellow-800 text-sm">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                <strong>Atención:</strong> Solo desbloquea manualmente si hay una razón válida (pago en efectivo, acuerdo especial, etc.)
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <p><strong>Estudiante:</strong> {selectedCompromiso?.estudiante_nombre}</p>
              <p><strong>Monto pendiente:</strong> ${selectedCompromiso?.monto?.toFixed(2)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Motivo del desbloqueo *</label>
              <Textarea
                placeholder="Indica el motivo del desbloqueo manual..."
                value={motivoDesbloqueo}
                onChange={(e) => setMotivoDesbloqueo(e.target.value)}
                className="mt-1"
                required
                data-testid="motivo-desbloqueo"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDesbloquearDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleDesbloquear}
              disabled={!motivoDesbloqueo.trim()}
              className="gap-1 bg-green-600 hover:bg-green-700"
              data-testid="btn-confirmar-desbloqueo"
            >
              <Unlock className="w-4 h-4" />
              Desbloquear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionCompromisosPago;
